import React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { AlertTriangle, Music, Clock } from 'lucide-react';

/**
 * Dialog to warn user about potential duplicate tracks
 * Updated to light/violet theme
 */
const DuplicateWarningDialog = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  onCancel,
  duplicates = [],
  uploadingFile = null 
}) => {
  const formatDuration = (seconds) => {
    if (!seconds) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent className="bg-white border-amber-200 max-w-lg shadow-lg">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
            <AlertTriangle className="w-5 h-5" />
            Piste potentiellement en double
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="text-gray-600">
              {uploadingFile && (
                <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-500 mb-1">Fichier à ajouter :</p>
                  <p className="text-gray-900 font-medium truncate">{uploadingFile}</p>
                </div>
              )}
              
              <p className="mb-3">
                Une ou plusieurs pistes similaires existent déjà dans cet événement :
              </p>
              
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {duplicates.map((dup, index) => (
                  <div 
                    key={dup.id || index}
                    className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200"
                  >
                    <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-violet-100 to-purple-100 rounded-lg flex items-center justify-center">
                      <Music className="w-5 h-5 text-violet-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-gray-900 font-medium truncate">{dup.title}</p>
                      <p className="text-gray-500 text-sm truncate">{dup.artist || 'Artiste inconnu'}</p>
                    </div>
                    {dup.duration && (
                      <div className="flex items-center gap-1 text-gray-500 text-sm">
                        <Clock className="w-3 h-3" />
                        {formatDuration(dup.duration)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex gap-2">
          <AlertDialogCancel 
            onClick={onCancel}
            className="bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200"
          >
            Annuler l&apos;ajout
          </AlertDialogCancel>
          <AlertDialogAction 
            onClick={onConfirm}
            className="bg-gradient-to-r from-violet-600 to-purple-600 text-white hover:from-violet-700 hover:to-purple-700"
          >
            Ajouter quand même
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DuplicateWarningDialog;
